import { Component } from '@theme/component';

/**
 * Homepage discount popup component that shows once per visitor
 * and reveals a discount code after email submission.
 * 
 * Uses localStorage with a 7-day expiry so the popup won't reappear
 * across tabs or page navigations within that window.
 *
 * @typedef {object} HomepageDiscountPopupRefs
 * @property {HTMLInputElement} emailInput - The email input field
 * @property {HTMLElement} initialView - The initial view with email form
 * @property {HTMLElement} successView - The success view with discount code
 * @property {HTMLElement} discountCode - The discount code element
 * @property {HTMLButtonElement} copyButton - The copy to clipboard button
 *
 * @extends {Component<HomepageDiscountPopupRefs>}
 */
class HomepageDiscountPopup extends Component {
  requiredRefs = ['emailInput', 'initialView', 'successView', 'discountCode', 'copyButton'];

  static STORAGE_KEY = 'homepage-discount-popup-shown';
  static SUPPRESS_DAYS = 7;

  connectedCallback() {
    super.connectedCallback();

    // Check if form was just submitted successfully
    // This signal is only present after a successful form submission
    const formSuccessSignal = this.querySelector('[ref="formSuccessSignal"]');
    
    if (formSuccessSignal) {
      console.log('[Homepage Popup] Form submitted successfully, showing success view');
      // Don't clear storage — keep popup suppressed after submission
      this.#showPopupWithSuccess();
      return;
    }

    // Check if popup has already been shown this session
    if (this.#hasBeenShown()) {
      console.log('[Homepage Popup] Already shown this session');
      return;
    }

    // Show popup after a short delay
    const delay = Number(this.dataset.delay) || 2000;
    console.log(`[Homepage Popup] Will show popup in ${delay}ms`);
    setTimeout(() => {
      this.#showPopup();
    }, delay);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  /**
   * Check if popup has been shown recently (within SUPPRESS_DAYS)
   * @returns {boolean}
   */
  #hasBeenShown() {
    try {
      const timestamp = localStorage.getItem(HomepageDiscountPopup.STORAGE_KEY);
      if (!timestamp) return false;
      const suppressUntil = Number(timestamp);
      if (Date.now() < suppressUntil) return true;
      // Expired — clear it
      localStorage.removeItem(HomepageDiscountPopup.STORAGE_KEY);
      return false;
    } catch {
      return false;
    }
  }

  /**
   * Mark popup as shown, suppressing it for SUPPRESS_DAYS
   */
  #markAsShown() {
    try {
      const suppressUntil = Date.now() + (HomepageDiscountPopup.SUPPRESS_DAYS * 24 * 60 * 60 * 1000);
      localStorage.setItem(HomepageDiscountPopup.STORAGE_KEY, String(suppressUntil));
    } catch {
      // localStorage unavailable — fail silently
    }
  }

  /**
   * Show the popup dialog
   */
  #showPopup() {
    console.log('[Homepage Popup] Attempting to show popup');
    
    // Find the dialog-component parent
    const dialogComponent = this.closest('dialog-component');
    console.log('[Homepage Popup] Dialog component found:', dialogComponent);
    
    if (dialogComponent && 'showDialog' in dialogComponent && typeof dialogComponent.showDialog === 'function') {
      console.log('[Homepage Popup] Showing dialog');
      dialogComponent.showDialog();
      this.#markAsShown();
    } else {
      console.error('[Homepage Popup] Could not find dialog component or showDialog method');
    }
  }

  /**
   * Show the popup dialog in success state (after form submission)
   */
  #showPopupWithSuccess() {
    const { initialView, successView } = this.refs;
    
    // Hide initial view, show success view
    initialView.classList.add('hidden');
    successView.classList.remove('hidden');
    
    // Show the dialog
    const dialogComponent = this.closest('dialog-component');
    if (dialogComponent && 'showDialog' in dialogComponent && typeof dialogComponent.showDialog === 'function') {
      console.log('[Homepage Popup] Showing dialog with success view');
      dialogComponent.showDialog();
      this.#markAsShown();
    } else {
      console.error('[Homepage Popup] Could not find dialog component or showDialog method');
    }
  }

  /**
   * Close the dialog
   */
  closeDialog = () => {
    const dialogComponent = this.closest('dialog-component');
    if (dialogComponent && 'closeDialog' in dialogComponent && typeof dialogComponent.closeDialog === 'function') {
      dialogComponent.closeDialog();
    } else {
      console.error('[Homepage Popup] Could not find dialog component or closeDialog method');
    }
  };

  /**
   * Copy discount code to clipboard
   */
  copyCode = async () => {
    const { discountCode, copyButton } = this.refs;
    const code = discountCode.textContent?.trim() || '';

    try {
      await navigator.clipboard.writeText(code);
      
      // Show feedback
      const originalText = copyButton.textContent;
      copyButton.textContent = 'Copied!';
      copyButton.classList.add('copied');

      setTimeout(() => {
        copyButton.textContent = originalText;
        copyButton.classList.remove('copied');
      }, 2000);
    } catch (error) {
      console.error('Copy failed:', error);
      // Fallback for older browsers
      this.#fallbackCopy(code);
    }
  };

  /**
   * Fallback copy method for older browsers
   * @param {string} text
   */
  #fallbackCopy(text) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    
    try {
      document.execCommand('copy');
      const { copyButton } = this.refs;
      const originalText = copyButton.textContent;
      copyButton.textContent = 'Copied!';
      
      setTimeout(() => {
        copyButton.textContent = originalText;
      }, 2000);
    } catch (error) {
      console.error('Fallback copy failed:', error);
    }
    
    textarea.remove();
  }
}

if (!customElements.get('homepage-discount-popup')) {
  customElements.define('homepage-discount-popup', HomepageDiscountPopup);
}
